import { DatabaseIcon, FileTextIcon, type LucideIcon, SearchIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSignOut } from '@/shared/hooks/use-sign-out';
import { useUser } from '@/shared/providers/user-context';
import { USER_ROLE, type UserRole } from '@/shared/types/user';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/shared/ui/sidebar';
import { Text } from '@/shared/ui/text';
import { cn } from '@/shared/utils/cn';

export type SidebarItem = {
  label: string;
  iconName: 'FileText' | 'Search' | 'Database';
  href: string;
  authorizedRoles: readonly UserRole[];
};

const iconMap: Record<SidebarItem['iconName'], LucideIcon> = {
  FileText: FileTextIcon,
  Search: SearchIcon,
  Database: DatabaseIcon,
};

const allSidebarItems: SidebarItem[] = [
  {
    label: '수탁사 기록서 만들기',
    iconName: 'FileText',
    href: '/cmo-records/1',
    authorizedRoles: [USER_ROLE.ADMIN, USER_ROLE.MANAGER],
  },
  {
    label: '기록서 조회',
    iconName: 'Search',
    href: '/records',
    authorizedRoles: [USER_ROLE.ADMIN, USER_ROLE.MANAGER, USER_ROLE.USER],
  },
  {
    label: 'DB 관리',
    iconName: 'Database',
    href: '/database',
    authorizedRoles: [USER_ROLE.ADMIN, USER_ROLE.MANAGER, USER_ROLE.USER],
  },
];

export function MainSidebarClient() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useUser();
  const { mutate: signOut } = useSignOut();

  const isActive = (href: string) => pathname.split('/')[1] === href.split('/')[1];

  const userRole: UserRole = user?.role ?? USER_ROLE.USER;
  const filteredItems = allSidebarItems.filter((item) => item.authorizedRoles.includes(userRole));

  const handleLogout = () => {
    signOut();
  };

  return (
    <Sidebar
      className="border-none bg-zinc-200"
      collapsible="icon"
    >
      <SidebarHeader className="p-0">
        <div className="flex items-center justify-between h-14 px-5">
          <img
            alt="logo"
            className="h-auto"
            height={20}
            src="/assets/auth/dk-logo-gnb.svg"
            width={200}
          />
        </div>
      </SidebarHeader>
      <SidebarContent className="p-2">
        <SidebarMenu>
          {filteredItems.map((item) => {
            const Icon = iconMap[item.iconName];
            return (
              <SidebarMenuItem key={item.href}>
                <SidebarMenuButton
                  className={cn(
                    'h-9',
                    isActive(item.href) && 'bg-sidebar-accent text-sidebar-accent-foreground'
                  )}
                  isActive={isActive(item.href)}
                  onClick={() => navigate(item.href)}
                  tooltip={item.label}
                >
                  <Icon />
                  <Text typography={isActive(item.href) ? 'typo-text-xl-medium' : 'typo-text-xl'}>
                    {item.label}
                  </Text>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter className="h-16 px-5 py-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex items-center gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-md p-1">
              <Avatar>
                <AvatarImage src={user?.avatar_url || undefined} />
                <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <Text typography="typo-text-xl">{user?.name}</Text>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="py-1 px-3 min-w-60">
            <DropdownMenuGroup className="flex flex-col gap-1 py-1.5 px-2">
              <DropdownMenuLabel className="p-0">
                <Text typography="typo-text-l-semibold">{user?.name}</Text>
              </DropdownMenuLabel>
              <DropdownMenuLabel
                asChild
                className="p-0"
              >
                <Text
                  className="text-muted-foreground"
                  typography="typo-text-m"
                >
                  {user?.email}
                </Text>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>설정</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Button
                className="mt-2"
                flex
                onClick={handleLogout}
                size="sm"
                variant="secondary"
              >
                로그아웃
              </Button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
